import React from 'react';
import { SPACE, RADII, FONT_SIZE, FONT_WEIGHT, FONTS, COLORS } from '../theme';
import './primitives.css';

interface ColorPickerProps {
  label: string;
  value: number; // hex number like 0xFF4500
  onChange: (value: number) => void;
}

// Exact-fidelity literals carried over from the prior styled
// definition — keeping these makes the de-style pass a visual no-op.
const ACCENT = '#fbbf24';
const LABEL_COLOR = '#a0aec0';

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: SPACE.md,
};

const labelStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.md,
  color: LABEL_COLOR,
  fontWeight: FONT_WEIGHT.medium,
};

const swatchGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
};

const swatchStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: `2px solid ${COLORS.border}`,
  borderRadius: RADII.md,
  cursor: 'pointer',
  padding: 0,
};

const hexInputStyle: React.CSSProperties = {
  width: 72,
  background: COLORS.surfaceSunken,
  border: `1px solid ${COLORS.border}`,
  color: ACCENT,
  padding: `${SPACE.xs}px ${SPACE.sm}px`,
  borderRadius: RADII.md,
  fontSize: FONT_SIZE.sm,
  fontFamily: FONTS.mono,
  outline: 'none',
};

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
const HEX_PATTERN_OPTIONAL_HASH = /^#?[0-9a-fA-F]{6}$/;

/** Convert 0xRRGGBB number to #RRGGBB string */
function numToHex(n: number): string {
  return '#' + (n & 0xFFFFFF).toString(16).padStart(6, '0');
}

/** Convert #RRGGBB string to 0xRRGGBB number */
function hexToNum(s: string): number {
  return parseInt(s.replace('#', ''), 16);
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ label, value, onChange }) => {
  const hexStr = numToHex(value);

  return (
    <div style={containerStyle}>
      <span style={labelStyle}>{label}</span>
      <div style={swatchGroupStyle}>
        <input
          className="helix-color-swatch"
          type="color"
          style={swatchStyle}
          value={hexStr}
          onChange={e => onChange(hexToNum(e.target.value))}
        />
        <input
          className="helix-hex-input"
          style={hexInputStyle}
          value={hexStr}
          onChange={e => {
            const v = e.target.value;
            if (HEX_PATTERN.test(v)) {
              onChange(hexToNum(v));
            }
          }}
          onBlur={e => {
            const v = e.target.value;
            if (HEX_PATTERN_OPTIONAL_HASH.test(v)) {
              onChange(hexToNum(v.startsWith('#') ? v : '#' + v));
            }
          }}
        />
      </div>
    </div>
  );
};
